import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  FileText,
  CheckCircle2,
  Trash2,
  X,
  History,
  Check,
  AlertCircle,
  MessageSquare,
  Sparkles,
} from "lucide-react";
import { t } from "@/i18n";
import { useStudio } from "@/store/StudioContext";
import { extractScriptTitle, isLikelyScriptMessage } from "@/domain/scriptSelectors";

/**
 * ScriptSelectionModal
 *
 * Modal amplio y adaptable para:
 * 1. Listar borradores del proyecto (guardados y disponibles en el historial del chat).
 * 2. Previsualizar el texto completo con scroll accesible sin cambiar la selección activa.
 * 3. Confirmar «Usar este guion para el Storyboard», guardando la versión si proviene del chat
 *    y fijándolo como guion activo del proyecto.
 * 4. Cambiar guion, quitar como activo y eliminar borradores guardados con confirmación.
 * 5. Cierre con X o Escape conservando el chat y sin guardar cambios no confirmados.
 */
export const ScriptSelectionModal = ({
  open,
  onClose,
  projectId,
  data: propsData,
  onScriptSelected,
  container,
}) => {
  const studio = useStudio();
  const dispatch = studio?.dispatch;
  const project = propsData?.project || (studio?.state?.projects || []).find((p) => p.id === projectId) || null;
  const stateScripts = studio?.state?.scripts;
  const scripts = useMemo(() => {
    return propsData?.scripts || (stateScripts || []).filter((sc) => sc.projectId === projectId);
  }, [propsData?.scripts, stateScripts, projectId]);
  const activeScript = useMemo(() => {
    return propsData?.activeScript || scripts.find((sc) => sc.id === project?.activeScriptId || sc.isActive) || null;
  }, [propsData?.activeScript, scripts, project?.activeScriptId]);
  const studioData = studio?.data;
  const thread =
    propsData?.thread ||
    (studio?.state?.threads || []).find((th) => th.projectId === projectId) ||
    (studioData?.project?.id === projectId || !studioData?.project?.id ? studioData?.thread : null) ||
    { messages: [] };

  // Tabs de navegación interna: "all" (guardados y detectados), "chat" (manual de todos los mensajes)
  const [activeTab, setActiveTab] = useState("auto"); // "auto" | "chat_all"

  // Selección actual en la ventana de previsualización (no altera el guion activo hasta confirmar)
  const [selectedItem, setSelectedItem] = useState(null);

  // Edición puntual de título / contenido al previsualizar
  const [editedTitle, setEditedTitle] = useState("");
  const [editedContent, setEditedContent] = useState("");

  // Estado de diálogo de confirmación para eliminar
  const [deleteConfirmTarget, setDeleteConfirmTarget] = useState(null);

  // Extraer mensajes de asistente que parezcan guiones
  const threadMessages = thread?.messages;
  const detectedChatDrafts = useMemo(() => {
    const msgs = threadMessages || [];
    return msgs
      .filter((m) => m.role === "assistant" && isLikelyScriptMessage(m))
      .map((m) => {
        const title = extractScriptTitle(m.content);
        return {
          id: `msg_${m.id}`,
          sourceMessageId: m.id,
          title,
          content: m.content,
          createdAt: m.createdAt || m.timestamp || null,
          isChatDraft: true,
        };
      });
  }, [threadMessages]);

  // Todos los mensajes del asistente (para selección manual si no fue reconocido automáticamente)
  const allAssistantMessages = useMemo(() => {
    const msgs = threadMessages || [];
    return msgs
      .filter((m) => m.role === "assistant" && m.content && m.content.trim().length > 20)
      .map((m) => {
        const title = extractScriptTitle(m.content);
        return {
          id: `manual_msg_${m.id}`,
          sourceMessageId: m.id,
          title,
          content: m.content,
          createdAt: m.createdAt || m.timestamp || null,
          isManual: true,
        };
      });
  }, [threadMessages]);

  // Al abrir el modal, inicializar selección con el guion activo si existe, o el primer guardado / detectado
  useEffect(() => {
    if (!open) {
      setSelectedItem(null);
      setDeleteConfirmTarget(null);
      return;
    }

    if (activeScript) {
      setSelectedItem({
        type: "saved",
        data: activeScript,
      });
      setEditedTitle(activeScript.title || "");
      setEditedContent(activeScript.content || "");
    } else if (scripts.length > 0) {
      setSelectedItem({
        type: "saved",
        data: scripts[0],
      });
      setEditedTitle(scripts[0].title || "");
      setEditedContent(scripts[0].content || "");
    } else if (detectedChatDrafts.length > 0) {
      setSelectedItem({
        type: "chat_draft",
        data: detectedChatDrafts[0],
      });
      setEditedTitle(detectedChatDrafts[0].title || "");
      setEditedContent(detectedChatDrafts[0].content || "");
    } else {
      setSelectedItem(null);
      setEditedTitle("");
      setEditedContent("");
    }
    // Solo inicializar al abrir o si cambia la cantidad/identidad base para evitar sobrescribir selección del usuario
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activeScript?.id, scripts.length, detectedChatDrafts.length]);

  // Cierre accesible por Escape sin confirmar cambios
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose?.();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  // Cambiar elemento previsualizado (solo cambia vista previa, no cambia guion activo)
  const handleSelectItem = (type, item) => {
    setSelectedItem({ type, data: item });
    setEditedTitle(item.title || "");
    setEditedContent(item.content || "");
  };

  // Confirmar selección para el Storyboard
  const handleConfirmUseForStoryboard = () => {
    if (!selectedItem) return;

    const { type, data } = selectedItem;
    const finalTitle = editedTitle.trim() || data.title || "Guion sin título";
    const finalContent = editedContent.trim() || data.content || "";

    if (!finalContent) return;

    if (type === "saved") {
      // Si el texto o título cambiaron, se guarda como nueva versión
      if (finalContent !== data.content || finalTitle !== data.title) {
        dispatch({
          type: "SCRIPT_SAVE",
          payload: {
            projectId,
            title: finalTitle,
            content: finalContent,
            sourceMessageId: data.sourceMessageId || null,
          },
        });
      } else {
        // Mismo texto: fijar como activo
        dispatch({
          type: "SCRIPT_SET_ACTIVE",
          payload: {
            projectId,
            scriptId: data.id,
          },
        });
      }
    } else {
      // Proviene del chat (detectado o manual): guardar y fijar como activo
      dispatch({
        type: "SCRIPT_SAVE",
        payload: {
          projectId,
          title: finalTitle,
          content: finalContent,
          sourceMessageId: data.sourceMessageId || null,
        },
      });
    }

    onScriptSelected?.({
      title: finalTitle,
      content: finalContent,
      sourceMessageId: data.sourceMessageId,
    });
    onClose?.();
  };

  // Quitar como activo (conserva la copia guardada intacta)
  const handleUnsetActive = (scriptId) => {
    dispatch({
      type: "SCRIPT_UNSET_ACTIVE",
      payload: {
        projectId,
        scriptId: scriptId || activeScript?.id,
      },
    });
  };

  // Iniciar confirmación de eliminación
  const handleRequestDelete = (script) => {
    setDeleteConfirmTarget(script);
  };

  // Ejecutar eliminación confirmada
  const handleConfirmDelete = () => {
    if (!deleteConfirmTarget) return;
    dispatch({
      type: "SCRIPT_DELETE",
      payload: {
        projectId,
        scriptId: deleteConfirmTarget.id,
      },
    });
    setDeleteConfirmTarget(null);

    // Ajustar selección si se eliminó el seleccionado
    if (selectedItem?.data?.id === deleteConfirmTarget.id) {
      const remaining = scripts.filter((s) => s.id !== deleteConfirmTarget.id);
      if (remaining.length > 0) {
        handleSelectItem("saved", remaining[0]);
      } else if (detectedChatDrafts.length > 0) {
        handleSelectItem("chat_draft", detectedChatDrafts[0]);
      } else {
        setSelectedItem(null);
      }
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose?.()}>
      <DialogContent
        data-testid="script-selection-modal"
        container={container}
        hideCloseButton={true}
        className="w-[95vw] sm:max-w-4xl max-h-[92vh] sm:max-h-[88vh] flex flex-col p-0 border-[#303030] bg-[#141414] text-[#f4f1ec] overflow-hidden rounded-xl shadow-2xl"
      >
        {/* Cabecera */}
        <DialogHeader className="flex flex-row items-center justify-between px-5 py-3.5 border-b border-[#242424] bg-[#181818] shrink-0 text-left">
          <div className="flex items-center gap-2.5 min-w-0 pr-3">
            <div className="h-8 w-8 rounded-lg bg-[#222] border border-[#333] flex items-center justify-center text-[#f5b544] shrink-0">
              <FileText size={16} />
            </div>
            <div className="min-w-0">
              <DialogTitle className="text-sm font-semibold text-[#f4f1ec] truncate">
                {t.assistant?.scriptSelectorTitle || "Selección de Guion"}
              </DialogTitle>
              <DialogDescription className="text-[11px] text-[#888] mt-0.5 truncate">
                {activeScript
                  ? `Guion activo actual: «${activeScript.title}» (${activeScript.versionId})`
                  : "Sin guion activo en el proyecto. Selecciona un borrador o copia guardada."}
              </DialogDescription>
            </div>
          </div>
          <button
            type="button"
            data-testid="script-selection-modal-close"
            onClick={onClose}
            className="rounded-lg p-1.5 text-[#888] hover:text-[#f4f1ec] hover:bg-[#262626] transition-colors shrink-0"
            title="Cerrar sin guardar (Escape)"
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        </DialogHeader>

        {/* Cuerpo del Modal: 2 columnas en desktop, vertical en móvil */}
        <div className="flex-1 min-h-0 flex flex-col md:flex-row overflow-hidden">
          {/* Columna Izquierda: Lista de Borradores y Copias Guardadas */}
          <div className="w-full md:w-80 border-b md:border-b-0 md:border-r border-[#242424] bg-[#161616] flex flex-col shrink-0 min-h-0">
            {/* Pestañas de origen */}
            <div className="flex items-center border-b border-[#242424] px-3 py-2 gap-2 text-xs bg-[#191919]">
              <button
                type="button"
                data-testid="script-tab-auto"
                onClick={() => setActiveTab("auto")}
                className={`px-2.5 py-1 rounded text-xs transition-colors ${
                  activeTab === "auto"
                    ? "bg-[#282828] text-[#f4f1ec] font-medium"
                    : "text-[#888] hover:text-[#ccc]"
                }`}
              >
                Guardados y Detectados
              </button>
              <button
                type="button"
                data-testid="script-tab-chat"
                onClick={() => setActiveTab("chat_all")}
                className={`px-2.5 py-1 rounded text-xs transition-colors flex items-center gap-1 ${
                  activeTab === "chat_all"
                    ? "bg-[#282828] text-[#f4f1ec] font-medium"
                    : "text-[#888] hover:text-[#ccc]"
                }`}
              >
                <MessageSquare size={12} />
                Historial del Chat
              </button>
            </div>

            {/* Lista scrollable */}
            <div className="flex-1 overflow-y-auto p-2.5 space-y-2">
              {activeTab === "auto" ? (
                <>
                  {/* Sección 1: Copias Guardadas */}
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#777] font-semibold px-2 py-1">
                      Copias guardadas ({scripts.length})
                    </div>
                    {scripts.length === 0 ? (
                      <div className="px-2 py-2 text-[11px] text-[#666] italic">
                        No hay copias guardadas aún en este proyecto.
                      </div>
                    ) : (
                      <div className="space-y-1 mt-1">
                        {scripts.map((sc) => {
                          const isCurrentActive =
                            sc.id === project?.activeScriptId || sc.isActive;
                          const isSelected =
                            selectedItem?.type === "saved" &&
                            selectedItem?.data?.id === sc.id;

                          return (
                            <div
                              key={sc.id}
                              data-testid={`script-item-saved-${sc.id}`}
                              onClick={() => handleSelectItem("saved", sc)}
                              className={`p-2.5 rounded-lg border text-left cursor-pointer transition-all ${
                                isSelected
                                  ? "border-[#f5b544] bg-[#221c12]"
                                  : "border-[#282828] bg-[#1a1a1a] hover:border-[#383838]"
                              }`}
                            >
                              <div className="flex items-center justify-between gap-1">
                                <span className="font-medium text-xs text-[#f4f1ec] truncate">
                                  {sc.title}
                                </span>
                                <div className="flex items-center gap-1 shrink-0">
                                  <span className="rounded bg-[#282828] px-1 py-0.2 text-[9px] font-mono text-[#aaa] border border-[#383838]">
                                    {sc.versionId || "v1"}
                                  </span>
                                  {isCurrentActive && (
                                    <span
                                      data-testid={`script-badge-active-${sc.id}`}
                                      className="rounded bg-[#064e3b]/40 text-[#34d399] px-1 py-0.2 text-[9px] font-medium border border-[#059669]/40 flex items-center gap-0.5"
                                    >
                                      <Check size={9} />
                                      Activo
                                    </span>
                                  )}
                                </div>
                              </div>
                              <p className="text-[11px] text-[#888] line-clamp-2 mt-1 leading-snug">
                                {sc.content}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Sección 2: Borradores Detectados en el Chat */}
                  <div className="mt-4">
                    <div className="text-[10px] uppercase tracking-wider text-[#777] font-semibold px-2 py-1 flex items-center gap-1">
                      <Sparkles size={11} className="text-[#f5b544]" />
                      Borradores detectados en el chat ({detectedChatDrafts.length})
                    </div>
                    {detectedChatDrafts.length === 0 ? (
                      <div className="px-2 py-2 text-[11px] text-[#666] italic">
                        No se detectaron guiones estructurados en los mensajes recientes.
                      </div>
                    ) : (
                      <div className="space-y-1 mt-1">
                        {detectedChatDrafts.map((draft) => {
                          const isSelected =
                            selectedItem?.type === "chat_draft" &&
                            selectedItem?.data?.sourceMessageId ===
                              draft.sourceMessageId;

                          // Verificar si ya existe una copia guardada para este mensaje
                          const existingSaved = scripts.find(
                            (s) => s.sourceMessageId === draft.sourceMessageId
                          );

                          return (
                            <div
                              key={draft.id}
                              data-testid={`script-item-chat-${draft.sourceMessageId}`}
                              onClick={() => handleSelectItem("chat_draft", draft)}
                              className={`p-2.5 rounded-lg border text-left cursor-pointer transition-all ${
                                isSelected
                                  ? "border-[#f5b544] bg-[#221c12]"
                                  : "border-[#282828] bg-[#1a1a1a] hover:border-[#383838]"
                              }`}
                            >
                              <div className="flex items-center justify-between gap-1">
                                <span className="font-medium text-xs text-[#f4f1ec] truncate">
                                  {draft.title}
                                </span>
                                {existingSaved && (
                                  <span className="rounded bg-[#202020] text-[#aaa] px-1 py-0.2 text-[9px] border border-[#333]">
                                    Guardado ({existingSaved.versionId})
                                  </span>
                                )}
                              </div>
                              <p className="text-[11px] text-[#888] line-clamp-2 mt-1 leading-snug">
                                {draft.content}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                /* Tab de Selección Manual desde todo el historial del chat */
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#777] font-semibold px-2 py-1">
                    Selección manual de mensaje ({allAssistantMessages.length})
                  </div>
                  <p className="text-[10px] text-[#777] px-2 mb-2 leading-relaxed">
                    Si un guion no fue reconocido de forma automática, elige el mensaje correspondiente para revisarlo y guardarlo como copia del proyecto.
                  </p>
                  <div className="space-y-1">
                    {allAssistantMessages.map((msg) => {
                      const isSelected =
                        selectedItem?.type === "chat_manual" &&
                        selectedItem?.data?.sourceMessageId === msg.sourceMessageId;

                      return (
                        <div
                          key={msg.id}
                          data-testid={`script-item-manual-${msg.sourceMessageId}`}
                          onClick={() => handleSelectItem("chat_manual", msg)}
                          className={`p-2.5 rounded-lg border text-left cursor-pointer transition-all ${
                            isSelected
                              ? "border-[#f5b544] bg-[#221c12]"
                              : "border-[#282828] bg-[#1a1a1a] hover:border-[#383838]"
                          }`}
                        >
                          <div className="flex items-center justify-between gap-1">
                            <span className="font-medium text-xs text-[#f4f1ec] truncate">
                              {msg.title}
                            </span>
                            <span className="text-[9px] text-[#777]">
                              {msg.createdAt
                                ? new Date(msg.createdAt).toLocaleTimeString([], {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })
                                : ""}
                            </span>
                          </div>
                          <p className="text-[11px] text-[#888] line-clamp-2 mt-1 leading-snug">
                            {msg.content}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Columna Derecha: Vista Previa Accesible y Acciones */}
          <div className="flex-1 flex flex-col bg-[#141414] min-h-0 overflow-hidden">
            {selectedItem ? (
              <div className="flex-1 flex flex-col p-4 sm:p-5 min-h-0 overflow-hidden">
                {/* Metadatos superiores de la selección */}
                <div className="flex flex-wrap items-center justify-between gap-2 pb-3 border-b border-[#242424] shrink-0">
                  <div className="flex-1 min-w-[200px]">
                    <label className="block text-[10px] uppercase font-semibold text-[#888] mb-1">
                      Título del Guion
                    </label>
                    <input
                      type="text"
                      data-testid="script-preview-title-input"
                      value={editedTitle}
                      onChange={(e) => setEditedTitle(e.target.value)}
                      placeholder="Título descriptivo del guion…"
                      className="w-full bg-[#1e1e1e] border border-[#333] rounded px-2.5 py-1 text-xs text-[#f4f1ec] focus:outline-none focus:border-[#f5b544]"
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    {/* Badge de tipo de borrador */}
                    {selectedItem.type === "saved" ? (
                      <span
                        data-testid="script-preview-badge-saved"
                        className="rounded bg-[#202020] text-[#f5b544] px-2 py-0.5 text-xs font-mono border border-[#333]"
                      >
                        Copia guardada ({selectedItem.data.versionId || "v1"})
                      </span>
                    ) : (
                      <span
                        data-testid="script-preview-badge-chat"
                        className="rounded bg-[#202020] text-[#aaa] px-2 py-0.5 text-xs border border-[#333]"
                      >
                        Borrador del chat (sin guardar)
                      </span>
                    )}

                    {/* Estado de activo */}
                    {selectedItem.type === "saved" &&
                      (selectedItem.data.id === project?.activeScriptId ||
                        selectedItem.data.isActive) && (
                        <span
                          data-testid="script-preview-badge-active"
                          className="rounded bg-[#064e3b]/40 text-[#34d399] px-2 py-0.5 text-xs font-medium border border-[#059669]/40 flex items-center gap-1"
                        >
                          <CheckCircle2 size={12} />
                          Guion Activo
                        </span>
                      )}
                  </div>
                </div>

                {/* Acciones de gestión para guion guardado: Quitar como activo / Eliminar */}
                {selectedItem.type === "saved" && (
                  <div className="flex items-center justify-between py-2 text-xs border-b border-[#242424] shrink-0">
                    <div>
                      {selectedItem.data.id === project?.activeScriptId ||
                      selectedItem.data.isActive ? (
                        <button
                          type="button"
                          data-testid="script-unset-active-btn"
                          onClick={() => handleUnsetActive(selectedItem.data.id)}
                          className="text-[#f5b544] hover:underline transition-colors"
                        >
                          Quitar como activo
                        </button>
                      ) : (
                        <span className="text-[11px] text-[#777]">
                          Este borrador guardado no es el guion activo actual.
                        </span>
                      )}
                    </div>

                    <button
                      type="button"
                      data-testid="script-delete-saved-btn"
                      onClick={() => handleRequestDelete(selectedItem.data)}
                      className="text-[#888] hover:text-[#ff8080] flex items-center gap-1 transition-colors"
                      title="Eliminar borrador guardado"
                    >
                      <Trash2 size={13} />
                      Eliminar borrador guardado
                    </button>
                  </div>
                )}

                {/* Confirmación explícita de eliminación */}
                {deleteConfirmTarget && (
                  <div
                    data-testid="script-delete-confirm-box"
                    className="my-2 p-3 rounded-lg border border-[#ff6666]/40 bg-[#251010] text-xs text-[#f4f1ec] shrink-0 space-y-2"
                  >
                    <div className="flex items-center gap-1.5 font-semibold text-[#ff8080]">
                      <AlertCircle size={14} />
                      ¿Eliminar este borrador guardado?
                    </div>
                    <p className="text-[11px] text-[#ccc] leading-relaxed">
                      {deleteConfirmTarget.id === project?.activeScriptId ||
                      deleteConfirmTarget.isActive
                        ? "Este guion es el activo del proyecto: al eliminar la copia guardada, el proyecto dejará de tener guion activo. El mensaje original del chat se conservará intacto."
                        : "Se eliminará la copia guardada del proyecto. El mensaje original del chat se conservará intacto."}
                    </p>
                    <div className="flex items-center gap-2 pt-1">
                      <button
                        type="button"
                        data-testid="script-delete-confirm-btn"
                        onClick={handleConfirmDelete}
                        className="rounded bg-[#991b1b] hover:bg-[#b91c1c] text-white px-3 py-1 font-medium transition-colors"
                      >
                        Confirmar eliminación
                      </button>
                      <button
                        type="button"
                        data-testid="script-delete-cancel-btn"
                        onClick={() => setDeleteConfirmTarget(null)}
                        className="rounded bg-[#2a2a2a] hover:bg-[#333] text-[#ccc] px-3 py-1 transition-colors"
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                )}

                {/* Área de texto completo con scroll accesible */}
                <div className="flex-1 min-h-0 flex flex-col mt-3 overflow-hidden">
                  <label className="block text-[10px] uppercase font-semibold text-[#888] mb-1">
                    Texto completo del guion (revisable)
                  </label>
                  <textarea
                    data-testid="script-preview-content-textarea"
                    value={editedContent}
                    onChange={(e) => setEditedContent(e.target.value)}
                    className="flex-1 w-full bg-[#181818] border border-[#2c2c2c] rounded-lg p-3 text-xs leading-relaxed font-mono text-[#d6d3cd] resize-none focus:outline-none focus:border-[#f5b544] overflow-y-auto"
                    placeholder="Contenido del guion…"
                  />
                </div>
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center p-6 text-center text-[#777] text-xs">
                Selecciona un guion de la lista de la izquierda para ver su contenido.
              </div>
            )}

            {/* Barra inferior fija con botón de confirmación */}
            <div className="p-3 sm:px-5 sm:py-3.5 border-t border-[#242424] bg-[#181818] flex items-center justify-between gap-3 shrink-0">
              <div className="text-[11px] text-[#888] truncate hidden sm:block">
                Seleccionar solo cambia la vista previa. El guion activo cambia al confirmar.
              </div>
              <div className="flex items-center gap-2 ml-auto">
                <button
                  type="button"
                  data-testid="script-modal-cancel-btn"
                  onClick={onClose}
                  className="v-btn-ghost v-btn-sm px-3 text-xs"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  data-testid="script-modal-confirm-btn"
                  disabled={!selectedItem || !editedContent.trim()}
                  onClick={handleConfirmUseForStoryboard}
                  className="v-btn-primary v-btn-sm px-4 text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50"
                >
                  <Check size={14} />
                  Usar este guion para el Storyboard
                </button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
